import { useRef, useState } from "react";
import { useSearchParams, useOutletContext } from "react-router";
import type { AppOutletContext } from "./root-layout";
import { apiFetch } from "@/api";
import type { RouteCountsResponse } from "@/types";
import { usePagedRoutes } from "@/hooks/use-paged-routes";
import { LogisticsMapView, type LogisticsMapViewHandle } from "@/features/map/logistics-map-view";
import { MapLoadingOverlay } from "@/features/map/map-loading-overlay";
import { MapEnvOverlay } from "@/features/map/map-env-overlay";
import { DateCountPicker } from "@/features/routes/date-count-picker";
import { RouteSidebar } from "@/features/routes/route-sidebar";

function todayLocalIso(): string {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

export default function ShipmentsPage() {
  const { sites } = useOutletContext<AppOutletContext>();
  const [searchParams, setSearchParams] = useSearchParams();
  const date = searchParams.get("date") ?? todayLocalIso();
  const mapRef = useRef<LogisticsMapViewHandle>(null);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  // Pinned routes (by path_id, in pin order) — the map's RouteOverlay is the source
  // of truth and pushes changes here via onPinnedChange; shared with the sidebar + deck.
  const [pinnedPathIds, setPinnedPathIds] = useState<readonly string[]>([]);

  const siteParam = sites.selectedSite ? `&site=${encodeURIComponent(sites.selectedSite)}` : "";
  // Only fetch once a real env is selected — otherwise MapEnvOverlay prompts for one.
  const streamUrl =
    sites.status === "ready" && sites.selectedSiteInfo
      ? `/api/routes/stream?date=${encodeURIComponent(date)}${siteParam}`
      : null;
  const { routes, unit, hasMore, loading, loadingMore, progress, error, loadMore } = usePagedRoutes(streamUrl);

  const fetchCounts = async (from: string, to: string): Promise<Record<string, number>> => {
    const resp = await apiFetch(`/api/route-counts?from=${from}&to=${to}${siteParam}`);
    if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
    return ((await resp.json()) as RouteCountsResponse).counts;
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto p-4">
      <div className="flex items-center gap-2">
        <DateCountPicker
          date={date}
          onChange={(d) =>
            setSearchParams(
              (prev) => {
                const next = new URLSearchParams(prev);
                next.set("date", d);
                return next;
              },
              { replace: true },
            )
          }
          fetchCounts={fetchCounts}
          cacheKey={`routes-${sites.selectedSite}`}
          noun="route"
        />
        <span className="text-sm text-muted-foreground">
          {error
            ? `Error: ${error}`
            : unit
              ? `${routes.length}${hasMore ? "+" : ""} route${routes.length === 1 && !hasMore ? "" : "s"} · ${unit}`
              : (sites.selectedSiteInfo?.name ?? "")}
        </span>
      </div>
      <div className="flex min-h-[720px] flex-1 overflow-hidden rounded-lg border">
        <RouteSidebar
          routes={routes}
          hoveredIndex={hoveredIndex}
          mapRef={mapRef}
          capacityUnit={unit ?? "m3"}
          pinnedPathIds={pinnedPathIds}
          hasMore={hasMore}
          loadingMore={loadingMore}
          onLoadMore={loadMore}
        />
        <div className="relative min-h-0 flex-1">
          <LogisticsMapView
            ref={mapRef}
            mode="routes"
            routes={routes}
            capacityUnit={unit ?? "m3"}
            onHover={setHoveredIndex}
            onPinnedChange={(ids) => setPinnedPathIds(ids)}
          />
          <MapLoadingOverlay loading={loading} progress={progress} />
          {sites.status === "ready" && !sites.selectedSiteInfo && <MapEnvOverlay />}
        </div>
      </div>
    </div>
  );
}
